import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './ProductionSchedule.css';

const ProductionSchedule = () => {
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('Momentum Forecast');
  const [productionData, setProductionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [newQuantity, setNewQuantity] = useState('');
  const [newDate, setNewDate] = useState('');

  // Fetch available scenarios from production_plan table
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/production-scenarios`);
        setScenarios(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        setError(`Failed to load scenarios: ${error.message}`);
      }
    };
    fetchScenarios();
  }, []);

  // Fetch production schedule data
  useEffect(() => {
    if (selectedScenario) {
      fetchProductionSchedule();
    }
  }, [selectedScenario]);

  const fetchProductionSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/production-schedule`, {
        params: { scenario_name: selectedScenario }
      });
      setProductionData(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching production schedule:', error);
      setError(`Failed to load production schedule: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsScheduled = async (rowId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/production-schedule/${rowId}/status`, {
        status: 'scheduled'
      });
      fetchProductionSchedule(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      setError(`Failed to update status: ${error.message}`);
    }
  };

  const handleMarkAsReceived = async (rowId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/production-schedule/${rowId}/status`, {
        status: 'received'
      });
      fetchProductionSchedule(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
      setError(`Failed to update status: ${error.message}`);
    }
  };

  const handleCancel = async (rowId) => {
    if (window.confirm('Are you sure you want to delete this production plan?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/production-schedule/${rowId}`);
        fetchProductionSchedule(); // Refresh data
      } catch (error) {
        console.error('Error deleting production plan:', error);
        setError(`Failed to delete production plan: ${error.message}`);
      }
    }
  };

  const handleChangeQuantity = async () => {
    if (!selectedRow || !newQuantity) return;
    
    try {
      await axios.put(`${API_BASE_URL}/api/production-schedule/${selectedRow}/quantity`, {
        production_quantity: parseInt(newQuantity)
      });
      setShowQuantityModal(false);
      setNewQuantity('');
      setSelectedRow(null);
      fetchProductionSchedule(); // Refresh data
    } catch (error) {
      console.error('Error updating quantity:', error);
      setError(`Failed to update quantity: ${error.message}`);
    }
  };

  const handleChangeDate = async () => {
    if (!selectedRow || !newDate) return;
    
    try {
      await axios.put(`${API_BASE_URL}/api/production-schedule/${selectedRow}/date`, {
        production_date: newDate
      });
      setShowDateModal(false);
      setNewDate('');
      setSelectedRow(null);
      fetchProductionSchedule(); // Refresh data
    } catch (error) {
      console.error('Error updating date:', error);
      setError(`Failed to update date: ${error.message}`);
    }
  };

  const openQuantityModal = (rowId, currentQuantity) => {
    setSelectedRow(rowId);
    setNewQuantity(currentQuantity.toString());
    setShowQuantityModal(true);
  };

  const openDateModal = (rowId, currentDate) => {
    setSelectedRow(rowId);
    setNewDate(currentDate);
    setShowDateModal(true);
  };

  return (
    <div className="production-schedule-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Production Schedule Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select scenario to view production schedule data</li>
              <li>Manage production status and quantities</li>
              <li>Match production plans with invoices</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">


        {/* Scenario Selector */}
        <div className="scenario-selector-pane">
          <div className="scenario-selector">
            <label htmlFor="scenario-select">Select Scenario:</label>
            <select
              id="scenario-select"
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              disabled={loading}
            >
              {scenarios.map((scenario) => (
                <option key={scenario} value={scenario}>
                  {scenario}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="production-schedule-content">
          <div className="production-schedule-header">
            <h1>Production Schedule Management</h1>
          </div>

          {error && (
            <div className="error-message" style={{ color: 'red', padding: '10px', marginBottom: '20px' }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && (
            <div className="loading">
              <p>Loading production schedule data...</p>
            </div>
          )}

          {!loading && !error && (
            <div className="schedule-table-container">
              <table className="schedule-table">
                <thead>
                  <tr>
                    <th>IGD SKU Number</th>
                    <th>IGD Name</th>
                    <th>Production Date</th>
                    <th>Production Quantity</th>
                    <th>Possible Invoice Match</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productionData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.IGD_SKU_number}</td>
                      <td>{row.IGD_Name}</td>
                      <td>{row.production_date}</td>
                      <td>{row.production_quantity}</td>
                      <td>
                        {row.invoice_match ? (
                          <span>
                            Invoice: {row.invoice_match.invoice_number}<br/>
                            Qty: {row.invoice_match.quantity}
                          </span>
                        ) : (
                          <span>No match found</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          {!row.invoice_match ? (
                            <>
                              <button
                                onClick={() => handleMarkAsScheduled(row.id)}
                                className="action-button scheduled"
                                disabled={row.status === 'scheduled'}
                              >
                                Mark as Scheduled
                              </button>
                              <button
                                onClick={() => openQuantityModal(row.id, row.production_quantity)}
                                className="action-button quantity"
                              >
                                Change Quantity
                              </button>
                              <button
                                onClick={() => handleCancel(row.id)}
                                className="action-button cancel"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleMarkAsReceived(row.id)}
                                className="action-button received"
                                disabled={row.status === 'received'}
                              >
                                Mark as Received
                              </button>
                              <button
                                onClick={() => openQuantityModal(row.id, row.production_quantity)}
                                className="action-button quantity"
                              >
                                Change Quantity
                              </button>
                              <button
                                onClick={() => openDateModal(row.id, row.production_date)}
                                className="action-button date"
                              >
                                Change Date
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quantity Modal */}
      {showQuantityModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Change Production Quantity</h2>
              <button className="modal-close-button" onClick={() => setShowQuantityModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <label>
                New Quantity:
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  min="1"
                />
              </label>
              <div className="modal-actions">
                <button onClick={handleChangeQuantity} className="confirm-button">Confirm</button>
                <button onClick={() => setShowQuantityModal(false)} className="cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Modal */}
      {showDateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Change Production Date</h2>
              <button className="modal-close-button" onClick={() => setShowDateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <label>
                New Date:
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </label>
              <div className="modal-actions">
                <button onClick={handleChangeDate} className="confirm-button">Confirm</button>
                <button onClick={() => setShowDateModal(false)} className="cancel-button">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionSchedule; 